import { LabelId } from '@this+that/models';
import { StateCreator } from 'zustand';

import {
  handleAddRecentlyInsertedId,
  handleArchiveUserCommunication,
  handleClearUserCommunicationsByEmail,
  handleDeleteDraft,
  handleDeleteUserCommunication,
  handleGetDraftWithThreadContext,
  handleGetMessageIdsByEmail,
  handleGetRichestUserCommunicationByDraftId,
  handleGetThreadRowByMessageId,
  handleGetUserCommunicationByDraftId,
  handleGetUserCommunicationByMessageId,
  handleGetUserCommunicationByThreadMessageId,
  handleInsertUserCommunication,
  handleInsertUserCommunicationIntoSlices,
  handleMarkViewTypesSynced,
  handleModifyUserCommunication,
  handleModifyUserCommunicationByThreadMessageId,
  handleReadUserCommunication,
  handleRemoveAndInsertUserCommunication,
  handleRemoveRecentlyInsertedId,
  handleRemoveUserCommunicationByMessageId,
  handleRemoveUserCommunicationsByMessageIds,
  handleResetStateForFilterChange,
  handleSaveAllUserCommunications,
  handleSaveIndividualUserCommunications,
  handleSetArchiveLoading,
  handleSetCreateLoading,
  handleSetDeleteLoading,
  handleSetForwardLoading,
  handleSetLoading,
  handleSetMarkAsReadLoading,
  handleSetMarkAsUnreadLoading,
  handleSetMoveToInboxLoading,
  handleSetReplyLoading,
  handleUnmarkViewTypesSynced,
  handleUnReadUserCommunication,
  handleUpdateEmailLabels,
  handleUpdateUserCommunicationContentFields,
  handleUpsertDraft,
} from './handlers';
import { UserCommunicationsStore } from './types';
import { useInboxEntriesStore } from '../InboxEntries/useInboxEntriesStore';

export const createUserCommunicationsSlice: StateCreator<
  UserCommunicationsStore
> = (set, get) => {
  return {
    primary: [],
    promotions: [],
    social: [],
    updates: [],
    forums: [],
    draft: [],
    sent: [],
    spam: [],
    trash: [],
    labels: {},
    all: [],
    unconfirmedDrafts: {},
    deletedDrafts: {},
    lastDeletedDraftSliceKeys: {},
    recentlyInsertedIds: [],
    syncedViewTypes: [],
    fetchGeneration: 0,
    isLoading: false,
    createLoading: false,
    forwardLoading: false,
    replyLoading: false,
    deleteLoading: false,
    archiveLoading: false,
    moveToInboxLoading: false,
    markAsReadLoading: false,
    markAsUnreadLoading: false,
    startKeys: {
      primary: null,
      promotions: null,
      social: null,
      updates: null,
      forums: null,
      draft: null,
      sent: null,
      spam: null,
      trash: null,
      labels: {},
      all: null,
    },
    setLoading: (isLoading) => handleSetLoading(set, isLoading),
    markViewTypesSynced: (viewTypes) =>
      handleMarkViewTypesSynced(set, viewTypes),
    unmarkViewTypesSynced: (viewTypes) =>
      handleUnmarkViewTypesSynced(set, viewTypes),
    setCreateLoading: (isLoading) => handleSetCreateLoading(set, isLoading),
    setForwardLoading: (isLoading) => handleSetForwardLoading(set, isLoading),
    setReplyLoading: (isLoading) => handleSetReplyLoading(set, isLoading),
    setDeleteLoading: (isLoading) => handleSetDeleteLoading(set, isLoading),
    setArchiveLoading: (isLoading) => handleSetArchiveLoading(set, isLoading),
    deleteDraft: (draftId) => {
      const result = handleDeleteDraft(set, draftId);
      useInboxEntriesStore
        .getState()
        .taskManager.activeTaskGroup.removeDraftFromInboxEntries(draftId);
      return result;
    },
    setMoveToInboxLoading: (isLoading) =>
      handleSetMoveToInboxLoading(set, isLoading),
    setMarkAsReadLoading: (isLoading) =>
      handleSetMarkAsReadLoading(set, isLoading),
    setMarkAsUnreadLoading: (isLoading) =>
      handleSetMarkAsUnreadLoading(set, isLoading),
    saveAllUserCommunications: (
      userCommunications,
      exclusiveStartKey,
      loadMore,
    ) =>
      handleSaveAllUserCommunications(
        set,
        userCommunications,
        exclusiveStartKey,
        loadMore,
      ),
    deleteUserCommunication: (messageId) =>
      handleDeleteUserCommunication(set, messageId),
    archiveUserCommunication: (messageId) =>
      handleArchiveUserCommunication(set, messageId),
    readUserCommunication: (messageId) =>
      handleReadUserCommunication(set, messageId),
    unReadUserCommunication: (messageId) =>
      handleUnReadUserCommunication(set, messageId),
    insertUserCommunication: (userCommunication) =>
      handleInsertUserCommunication(set, userCommunication),
    modifyUserCommunication: (userCommunication) =>
      handleModifyUserCommunication(set, userCommunication),
    updateUserCommunicationContentFields: (messageId, fields) =>
      handleUpdateUserCommunicationContentFields(set, messageId, fields),
    removeUserCommunicationByMessageId: (messageId) =>
      handleRemoveUserCommunicationByMessageId(set, get, messageId),
    removeUserCommunicationsByMessageIds: (messageIds) =>
      handleRemoveUserCommunicationsByMessageIds(set, get, messageIds),
    removeAndInsertUserCommunication: (userCommunication) =>
      handleRemoveAndInsertUserCommunication(set, userCommunication),
    insertUserCommunicationIntoSlices: (userCommunication, sliceKeys) =>
      handleInsertUserCommunicationIntoSlices(
        set,
        userCommunication,
        sliceKeys,
      ),
    upsertDraft: (draftData, messageId, inboxEntryId) =>
      handleUpsertDraft(set, draftData, messageId, inboxEntryId),
    modifyUserCommunicationByThreadMessageId: (userCommunication) =>
      handleModifyUserCommunicationByThreadMessageId(set, userCommunication),
    updateUserCommunicationLabels: (messageId, newLabelIds) =>
      handleUpdateEmailLabels(set, messageId, newLabelIds as LabelId[]),
    addRecentlyInsertedId: (id) => handleAddRecentlyInsertedId(set, id),
    removeRecentlyInsertedId: (id) => handleRemoveRecentlyInsertedId(set, id),
    clearUserCommunicationsByEmail: (userEmailAddress, platform) =>
      handleClearUserCommunicationsByEmail(set, userEmailAddress, platform),
    saveIndividualUserCommunications: (
      userCommunications,
      exclusiveStartKey,
      viewType,
      loadMore,
      queriedViewTypes,
    ) =>
      handleSaveIndividualUserCommunications(
        set,
        userCommunications,
        exclusiveStartKey,
        viewType,
        loadMore,
        queriedViewTypes,
      ),
    getUserCommunicationByMessageId: (messageId) =>
      handleGetUserCommunicationByMessageId(get, messageId),
    getThreadRowByMessageId: (messageId) =>
      handleGetThreadRowByMessageId(get, messageId),
    getUserCommunicationByThreadMessageId: (threadMessageId) =>
      handleGetUserCommunicationByThreadMessageId(get, threadMessageId),
    getMessageIdsByEmail: (userEmailAddress, platform) =>
      handleGetMessageIdsByEmail(get, userEmailAddress, platform),
    getUserCommunicationByDraftId: (draftId) =>
      handleGetUserCommunicationByDraftId(get, draftId),
    getDraftWithThreadContext: (draftId) =>
      handleGetDraftWithThreadContext(get, draftId),
    getRichestUserCommunicationByDraftId: (draftId) =>
      handleGetRichestUserCommunicationByDraftId(get, draftId),
    resetStateForFilterChange: () => handleResetStateForFilterChange(set),
    resetState: () =>
      set((state) => ({
        primary: [],
        promotions: [],
        social: [],
        updates: [],
        forums: [],
        draft: [],
        sent: [],
        spam: [],
        trash: [],
        labels: {},
        all: [],
        unconfirmedDrafts: {},
        deletedDrafts: {},
        lastDeletedDraftSliceKeys: {},
        recentlyInsertedIds: [],
        syncedViewTypes: [],
        fetchGeneration: state.fetchGeneration + 1,
        isLoading: false,
        createLoading: false,
        forwardLoading: false,
        replyLoading: false,
        deleteLoading: false,
        archiveLoading: false,
        moveToInboxLoading: false,
        markAsReadLoading: false,
        markAsUnreadLoading: false,
        startKeys: {
          primary: null,
          promotions: null,
          social: null,
          updates: null,
          forums: null,
          draft: null,
          sent: null,
          spam: null,
          trash: null,
          labels: {},
          all: null,
        },
      })),
  };
};
